import { Mascot as MascotzMascot } from "mascotz/react";
import { cn } from "@/lib/utils";
import type { MascotMood, MascotProps } from "./types";
import { MASCOT_ARIA_LABEL, MASCOT_MESSAGE, MASCOT_SIZE_PX } from "./types";
import { MASCOT_BODY, MASCOT_COLOR, MASCOT_EYES, MASCOT_MOOD, MASCOT_SEED } from "./mascotz-config";

// ─── Speech bubble ────────────────────────────────────────────────────────────

function SpeechBubble({ message }: { message: string }) {
    return (
        <div
            role="status"
            aria-live="polite"
            className={cn(
                "absolute left-[calc(100%+14px)] top-1/2 z-30 max-w-[210px] -translate-y-1/2",
                "rounded-2xl border border-stone-200 bg-white px-3.5 py-2.5",
                "text-[13px] leading-[1.5] text-stone-900 shadow-sm",
                "dark:border-stone-700 dark:bg-stone-800 dark:text-stone-100",
                "pointer-events-none",
            )}
        >
            <span
                aria-hidden="true"
                className="absolute -left-2 top-1/2 h-0 w-0 -translate-y-1/2 border-y-8 border-y-transparent border-r-8 border-r-stone-200 dark:border-r-stone-700"
            />
            <span
                aria-hidden="true"
                className="absolute -left-[7px] top-1/2 h-0 w-0 -translate-y-1/2 border-y-8 border-y-transparent border-r-8 border-r-white dark:border-r-stone-800"
            />
            {message}
        </div>
    );
}

// ─── Main component ──────────────────────────────────────────────────────────

export const Mascot = ({
    mood,
    size = "md",
    className,
    ariaLabel,
    showBubble = false,
    message,
}: MascotProps) => {
    const px = MASCOT_SIZE_PX[size];
    const displayMessage = message ?? MASCOT_MESSAGE[mood];
    const label = ariaLabel ?? `${MASCOT_ARIA_LABEL[mood]}. ${displayMessage}`;
    // Decorative unless someone labelled it or it is talking (the bubble is a
    // live region, so it has to be reachable).
    const decorative = ariaLabel === undefined && !showBubble;

    return (
        <div
            data-testid="mascot"
            data-mood={mood}
            className={cn("relative inline-block select-none pointer-events-none", className)}
            style={{ width: px, height: px }}
            aria-hidden={decorative ? true : undefined}
        >
            <MascotzMascot
                seed={MASCOT_SEED}
                body={MASCOT_BODY}
                eyes={MASCOT_EYES}
                color={MASCOT_COLOR}
                mood={MASCOT_MOOD[mood]}
                size={px}
                aria-label={decorative ? undefined : label}
            />
            {showBubble && <SpeechBubble message={displayMessage} />}
        </div>
    );
};

Mascot.displayName = "Mascot";
export type { MascotMood };